import { useParams } from 'react-router-dom';
import { Spin, Tabs } from 'antd';
import {
  AndroidOutlined,
  AppleOutlined,
  ControlOutlined,
  ScheduleOutlined,
  InfoCircleOutlined,
  SlidersOutlined,
  MonitorOutlined,
  CloudDownloadOutlined,
  BugOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { useUserData } from '../../../../../../Hooks/TT_ELD/Users';
import MainFields from './MainFields';
import Commands from './Commands';
import Config from './Config';
import Logs from '../Logs';
import TruckInfo from '../TruckInfo/TruckInfo';
import LastTracking from './LastTracking';
import Documents from './Documents';
import UserCompanies from '../../../UserCompanies/UserCompanies';
import VehicleTabs from '../../Vehicles/VehicleData/VehicleEdit/VehicleTabs';
import TrackingByRang from './TrackingByRang';
import FMCSADriver from './FMCSA/FMCSADriver';
import MalfunctionTab from './Malfunction/MalfunctionTab';
import DotProgressDetails from './DotProgressDetails';
import { useTagQuerySync } from '../../../../../../Utils/useTagQuerySync';
import useRouterQueryTabs from '../../../../../../Utils/useRouterQueryTabs';
import {
  isWhiteLabelAdmin,
  WHITE_LABEL_HIDDEN_DRIVER_TABS,
} from '../../../../../../Utils/whiteLabel';

const getUserFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const User = () => {
  const { id } = useParams();
  const user = getUserFromStorage();
  const canViewDotProgressDetailsTab = ['support', 'support_manager', 'developer', 'manager'].includes(
    user?.role || '',
  );
  const isWhiteLabel = isWhiteLabelAdmin();
  const queryKey = `tabKey_user_${id}`;
  const queryDate = `date_user_${id}`;
  const queryStatus = `status_user_${id}`;
  const [tabKey, setTabKey] = useRouterQueryTabs('1', queryKey, String);
  const [date, setDate] = useRouterQueryTabs('', queryDate, String);
  const [recordStatus, setRecordStatus] = useRouterQueryTabs('1', queryStatus, String);

  useTagQuerySync(
    { en_US: `User #${id}`, zh_CN: '' },
    `user/users/${id}`,
    { [queryKey]: tabKey, [queryDate]: date, [queryStatus]: recordStatus },
    {
      [queryKey]: setTabKey,
      [queryDate]: setDate,
      [queryStatus]: setRecordStatus,
    },
  );

  const { data } = useUserData(id);

  const items = [
    {
      label: 'Main fields',
      key: '1',
      children: <MainFields />,
      icon: <AppleOutlined rev={undefined} />,
    },
    {
      label: <span>Commands</span>,
      key: '2',
      children: <Commands />,
      icon: <AndroidOutlined rev={undefined} />,
    },
    {
      label: <span>Config</span>,
      key: '3',
      children: <Config />,
      icon: <ControlOutlined rev={undefined} />,
    },

    {
      label: <span>Logs</span>,
      key: '4',
      children: (
        <Logs
          calendar={date}
          setCalendar={setDate}
          recordStatus={recordStatus}
          setRecordStatus={setRecordStatus}
        />
      ),
      icon: <ScheduleOutlined rev={undefined} />,
    },
    {
      label: <span>Truck info</span>,
      key: '5',
      children: <TruckInfo />,
      icon: <InfoCircleOutlined rev={undefined} />,
    },
    {
      label: <span>Vehicle</span>,
      key: '6',
      children: <VehicleTabs vehicleId={data?.vehicleId} />,
      icon: <SlidersOutlined rev={undefined} />,
    },
    {
      label: <span>Last tracking</span>,
      key: '7',
      children: <LastTracking />,
      icon: <MonitorOutlined rev={undefined} />,
    },
    {
      label: <span>Documents</span>,
      key: '8',
      children: <Documents />,
      icon: <CloudDownloadOutlined rev={undefined} />,
    },
    {
      label: <span>Tracking By Range</span>,
      key: '9',
      children: <TrackingByRang />,
      icon: <CloudDownloadOutlined rev={undefined} />,
    },
    {
      label: <span>FMCSA Driver</span>,
      key: '10',
      children: <FMCSADriver uid={data?.company?.uid} />,
      icon: <CloudDownloadOutlined rev={undefined} />,
    },
    {
      label: <span>Malfunction</span>,
      key: '11',
      children: <MalfunctionTab />,
      icon: <ToolOutlined rev={undefined} />,
    },
    ...(canViewDotProgressDetailsTab
      ? [
          {
            label: <span>DOT Progress Details</span>,
            key: '12',
            children: <DotProgressDetails driverUid={data?.uid} />,
            icon: <BugOutlined rev={undefined} />,
          },
        ]
      : []),
  ];
  const items2 = [
    {
      label: <span>Main fields</span>,
      key: '1',
      children: <MainFields />,
      icon: <AppleOutlined rev={undefined} />,
    },
    {
      label: <span>Commands</span>,
      key: '2',
      children: <Commands />,
      icon: <AndroidOutlined rev={undefined} />,
    },
    {
      label: <span>Config</span>,
      key: '3',
      children: <Config />,
      icon: <ControlOutlined rev={undefined} />,
    },
    {
      label: 'Company',
      key: '4',
      children: <UserCompanies uid={id} />,
      icon: <CloudDownloadOutlined rev={undefined} />,
    },
  ];
  const driverTabs = isWhiteLabel
    ? items.filter((tab) => !WHITE_LABEL_HIDDEN_DRIVER_TABS.includes(tab.key))
    : items;

  const getTabs = data?.role === 'driver' ? driverTabs : items2;
  const safeActiveKey = getTabs.some((tab) => tab.key === tabKey) ? tabKey : getTabs[0]?.key;

  return (
    <Tabs
      activeKey={safeActiveKey}
      onChange={setTabKey}
      style={{ height: '100%' }}
      items={getTabs.map((tab) => {
        return {
          label: tab.label,
          key: tab.key,
          children: tab.children,
          icon: tab?.icon,
        };
      })}
    />
  );
};

export default User;
